// Simplified KnowledgeBase component with only the clear button

import React from 'react';
import { IconButton, Tooltip, Typography, List, ListItem, ListItemText, ListItemIcon, Stack } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { useTheme } from '@mui/material/styles';
import { MdStorage, MdInsertDriveFile, MdLink } from 'react-icons/md';
import useWebSocketContext from '../hooks/useWebSocketContext';
import { resetKbItems } from '../../store/slices/kbSlice';
import { motion } from 'framer-motion';

const KnowledgeBase: React.FC = () => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const { session_id, user_id } = useSelector((state: RootState) => state.session);
    const { sendToWs } = useWebSocketContext();
    const kbItems = useSelector((state: RootState) => state.kb.items);

    const handleClearKb = () => {
        const msgToSend = JSON.stringify({
            method: 'clear_kb',
            params: {
                session_id: session_id,
                user_id: user_id,
                agent: 'keai'
            }
        });

        sendToWs(msgToSend);
        dispatch(resetKbItems());
    };

    if (kbItems.length === 0) {
        return null;
    }

    return (
        <Stack
            spacing={2}
            sx={{
                width: '100%',
                height: '100%',
                overflow: 'auto',
            }}
        >
            <List sx={{ 
                width: '100%',
                flex: 1,
                overflow: 'auto',
                alignContent: "end",
                padding: '0 8px',
                '&::-webkit-scrollbar': {
                    width: '4px',
                },
                '&::-webkit-scrollbar-track': {
                    background: 'transparent',
                },
                '&::-webkit-scrollbar-thumb': {
                    background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
                    borderRadius: '4px',
                },
            }}>
                {kbItems.map((item, index) => (
                    <ListItem
                        key={item.id}
                        component={motion.div}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.2, delay: index * 0.05 }}
                        sx={{
                            padding: '4px 8px',
                            borderRadius: '6px',
                            marginBottom: '2px',
                            backgroundColor: theme.palette.mode === 'dark' 
                                ? 'rgba(255,255,255,0.05)' 
                                : 'rgba(0,0,0,0.02)',
                            '&:hover': {
                                backgroundColor: theme.palette.mode === 'dark' 
                                    ? 'rgba(255,255,255,0.08)' 
                                    : 'rgba(0,0,0,0.04)',
                            }
                        }}
                    >
                        <ListItemText
                            primary={item.fileName}
                            sx={{
                                '& .MuiListItemText-primary': {
                                    fontSize: '0.75rem',
                                    color: theme.palette.text.primary,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }
                            }}
                        />
                        <ListItemIcon sx={{ minWidth: '28px', marginLeft: '8px' }}>
                            {item.type === 'url' ? (
                                <MdLink size={18} color={theme.palette.primary.main} />
                            ) : (
                                <MdInsertDriveFile size={18} color={theme.palette.primary.main} />
                            )}
                        </ListItemIcon>
                    </ListItem>
                ))}
            </List>

            <Stack 
                alignItems="center" 
                spacing={0.5}
                sx={{ mt: 1 }}
            >
                <Tooltip title="Clear KB">
                    <IconButton
                        onClick={handleClearKb}
                        sx={{
                            color: theme.palette.primary.main,
                            transition: 'all 0.2s ease',
                            '&:hover': {
                                backgroundColor: 'transparent',
                            },
                            padding: 0
                        }}
                    >
                        <MdStorage size={20} />
                    </IconButton>
                </Tooltip>
                <Typography variant="subtitle1" sx={{ 
                    fontWeight: 500, 
                    fontSize: '0.85rem', 
                    color: theme.palette.text.disabled,
                }}>
                    Knowledge Base
                </Typography>
            </Stack>
        </Stack>
    );
};

export default KnowledgeBase;